<template>
	<v-card tile elevation="0" style="height: 100%; overflow-y: hidden">
		<v-container v-if="labCopy === null" fluid class="fill-height justify-center align-center">
			<v-progress-circular indeterminate :size="100" width="6" color="primary" />
		</v-container>

		<v-form v-else>
			<v-card-title class="card-header text-left fixed-card-title2">
				<v-row no-gutters>
					<v-col class="align-center d-flex" cols="10">
						<v-toolbar-title>{{ $t('edit-labs-header') }}</v-toolbar-title>
					</v-col>
					<v-spacer />
					<v-icon color="white" @click="closeDrawer">mdi-close</v-icon>
				</v-row>
			</v-card-title>

			<v-card-text :key="key" class="pt-4 pb-0 overflow-y-auto" style="height: calc(100vh - 205px)">
				<v-row dense class="ma-0">
					<v-col cols="12">
						<v-select
							v-model="labCopy.reviewedBy"
							:label="$t('reviewed-by')"
							:items="medicalStaff"
							item-text="fullName"
							outlined
							dense
							hide-details
							return-object
							:menu-props="{ maxHeight: 200, offsetY: true }">
							<template #item="{ item }">
								<span style="width: 100%">{{ item.fullName }}</span>
							</template>
						</v-select>
					</v-col>
					<v-col cols="12">
						<v-text-field v-model="labCopy.specimenType" :label="$t('specimen-type')" outlined dense hide-details />
					</v-col>
					<v-col cols="12">
						<v-text-field v-model="labCopy.panelGroup" :label="$t('panel-group')" outlined dense hide-details />
					</v-col>
					<v-col cols="12">
						<v-text-field v-model="labCopy.testName" :label="$t('test')" outlined dense hide-details />
					</v-col>
					<v-col cols="6">
						<v-text-field v-model="labCopy.value" :label="$t('value')" outlined dense hide-details />
					</v-col>
					<v-col cols="6">
						<v-text-field v-model="labCopy.unit" :label="$t('unit')" outlined dense disabled hide-details />
					</v-col>
					<v-col cols="9">
						<v-text-field v-model="labCopy.interpretation" :label="$t('interpretation')" outlined dense hide-details />
					</v-col>
					<v-col cols="3" class="d-flex justify-center">
						<v-checkbox v-model="labCopy.isCritical" :label="$t('is-critical')" dense hide-details />
					</v-col>
					<v-col cols="12">
						<v-text-field
							v-model="labCopy.relatedDiagnosisCode"
							:label="$t('related-diagnosis-code')"
							outlined
							dense
							hide-details />
					</v-col>
					<v-col cols="12">
						<v-menu
							v-model="dateMenu"
							:close-on-content-click="false"
							transition="scale-transition"
							offset-y
							min-width="auto">
							<template #activator="{ on, attrs }">
								<v-text-field
									:value="formattedDate"
									:label="$t('lab-date')"
									prepend-inner-icon="mdi-calendar"
									readonly
									outlined
									dense
									hide-details
									v-bind="attrs"
									v-on="on" />
							</template>
							<v-date-picker v-model="labCopy.labDate" no-title @input="dateMenu = false" />
						</v-menu>
					</v-col>
				</v-row>
			</v-card-text>

			<v-container class="px-4">
				<v-row dense class="ma-0">
					<v-col cols="12" class="mx-0">
						<InputTimeFormatter
							:inputValue="timeSpentSeconds"
							@update:inputValue="(val) => (timeSpentSeconds = val)"
							:inputLabel="$t('time-spent')" />
					</v-col>
				</v-row>
				<v-row dense class="ma-0">
					<v-col cols="6" order="1">
						<v-btn @click="closeDrawer()" color="secondary" style="color: black" block>{{ $t('cancel') }}</v-btn>
					</v-col>
					<v-tooltip top v-if="!editButtonEnabled">
						<template #activator="{ on, attrs }">
							<v-col cols="6" v-bind="attrs" v-on="on" order="2">
								<v-btn disabled block>{{ $t('edit') }}</v-btn>
							</v-col>
						</template>
						<span>{{ $t('fill-all-fields-warning') }}</span>
					</v-tooltip>
					<v-col cols="6" v-else order="2">
						<v-btn @click="editLab()" color="dark_purple" style="color: white" block>
							{{ $t('edit') }}
						</v-btn>
					</v-col>
				</v-row>
			</v-container>
		</v-form>
	</v-card>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { getRowsForDataTable, formatTimestamp } from '../../utils/utilities';
import InputTimeFormatter from '../Client/components/InputTimeFormatter.vue';

export default {
	name: 'CCMLabsEdit',
	components: { InputTimeFormatter },
	data () {
		return {
			labCopy: null,
			key: false,
			medicalStaff: null,
			timeSpentSeconds: 0,
			relatedDiagnosisCode: '',
			dateMenu: false,
		};
	},
	computed: {
		...mapState({
			detailedUser: (state) => state.users.detailedUser,
			currentRecord: (state) => state.ccmRecords.ccmRecord,
			roleList: (state) => state.roles.roles,
			selectedCCMLab: (state) => state.ccmLabs.ccmLab,
		}),

		editButtonEnabled () {
			return (
				this.timeSpentSeconds &&
				this.labCopy.reviewedBy &&
				this.labCopy.value.trim() &&
				this.labCopy.labDate.trim() &&
				this.timeSpentSeconds <= 7200 && // 2 hours in seconds
				this.labCopy.testName.trim() &&
				this.labCopy.panelGroup &&
				(this.selectedLabPanelGroup !== 'Other' || this.otherLabPanelGroup.trim()) &&
				this.labCopy.specimenType &&
				(this.selectedLabSpecimenType !== 'Other' || this.otherLabSpecimenType.trim()) &&
				this.labCopy.unit &&
				(this.selectedLabUnit !== 'Other' || this.otherLabUnit.trim()) &&
				this.labCopy.interpretation &&
				(this.selectedLabInterpretation !== 'Other' || this.otherLabInterpretation.trim())
			);
		},

		formattedDate () {
			return this.formatTimestamp(this.labCopy.labDate);
		},
	},

	methods: {
		...mapActions({
			updateCCMLab: 'ccmLabs/updateCCMLab',
			getCCMLabsByPatientId: 'ccmLabs/getCCMLabsByPatientId',
		}),
		getRowsForDataTable,
		formatTimestamp,

		selectedLabCopy () {
			this.labCopy = { ...this.selectedCCMLab };
		},

		closeDrawer () {
			this.timeSpentSeconds = 0;
			this.dateMenu = false;
			this.key = !this.key;
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', false);
			this.$store.commit('SET_EDIT_DETAILS_DRAWER', false);
		},

		async editLab () {
			this.labCopy.timeSpentSeconds = this.timeSpentSeconds;
			this.labCopy.panelGroup = this.labCopy.panelGroup.name;
			this.labCopy.referenceRange = 'string';
			this.labCopy.labSource = 'string';
			delete this.labCopy.id;
			delete this.labCopy.patientId;
			delete this.labCopy.ccmRecordId;
			delete this.labCopy.patientName;
			delete this.labCopy.performedBy;
			delete this.labCopy.hospitalName;

			const request = {
				id: this.selectedCCMLab.id,
				body: this.labCopy,
			};

			this.updateCCMLab(request);
			// this.getCCMLabsByPatientId(this.currentRecord.patientId);

			this.closeDrawer();
		},

		filterMedicalStaff () {
			return this.detailedUser.userData.medicalTeamsData[0].membersList
				.filter((member) => (member.roleId === 3 || member.roleId === 4) && member.isSequoiaUser && member.status === 1)
				.map((member) => ({
					...member,
					fullName: `${member.firstName} ${member.lastName}`,
				}));
		},

		filterNumbersOnly (inputEvent) {
			if (/\D/.test(inputEvent.key)) {
				inputEvent.preventDefault();
			}
			return true;
		},
	},

	async mounted () {
		this.medicalStaff = this.filterMedicalStaff();
		this.selectedLabCopy();
	},

	watch: {
		selectedCCMLab () {
			this.selectedLabCopy();
			this.key = !this.key;
		},
	},
};
</script>
